// Enum types based on API documentation
export type SeverityType = 'low' | 'medium' | 'high' | 'critical';
export type StatusType = 'new' | 'investigating' | 'resolved' | 'false_positive';
export type QuickFilterType = 'domain' | 'corporate' | 'thirdparty' | 'email' | 'ip';
export type SearchTypeType = 'all' | 'breaches' | 'stealer';
export type SourceType = 'stealer' | 'breach' | 'darkweb' | 'osint';
export type BulkActionType = 'update' | 'delete';
export type RoleType = 'admin' | 'member';
import type React from "react";

export interface PasswordUnmaskSearchRequest {
    fetch_id: string;           // UUID from search results
    result_index: number;       // Index of result in search results array
}

// For saved incidents - requires record_id and log_id
export interface PasswordUnmaskSavedRequest {
    record_id: number;          // Incident ID from database
    log_id?: number | null;             // Log ID from the original data source
}

// Union type for the request - API will determine context based on provided fields
export interface PasswordUnmaskRequest {
    // For search results
    fetch_id?: string;
    result_index?: number;
    // For saved incidents
    record_id?: number;
    log_id?: number | null;
}

// Updated response type based on new documentation
export interface PasswordUnmaskResponse {
    success: boolean;
    password: string;
    unmasks_remaining: number;
    source: string;                    // New field
    log_id: string;                   // New field
    previously_unmasked: boolean;     // New field
}

export interface PasswordUnmaskError {
    success: false;
    error: string;
    details?: string;
}

// Helper type to determine unmask context
export type UnmaskContext = 'search' | 'saved';

// Helper type for unmask availability
export interface UnmaskAvailability {
    canUnmask: boolean;
    reason?: string;
    context: UnmaskContext;
}

// Enhanced threat intel record to include log_id (for saved incidents)
export interface ThreatIntelRecordDetail {
    id: number; // readonly
    data_type?: string | null; // maxLength: 50
    url?: string | null; // maxLength: 500, format: uri
    log_id?: number | null; // Now required for unmasking saved records
    username?: string | null; // maxLength: 255
    password?: string | null; // maxLength: 255
    log_date?: string | null; // format: date
    hostname?: string | null; // maxLength: 255
    ip?: string | null; // minLength: 1
    malware_path?: string | null;
    anti_virus?: string | null; // maxLength: 255
    severity: SeverityType; // required field, enum with 4 values
    status: StatusType; // required field, enum with 4 values
    tags: Tags; // required field
    notes?: string | null;
    source?: string | null; // maxLength: 255
    saved_at: string; // readonly, format: date-time
    updated_at: string; // readonly, format: date-time
    organization: number; // readonly
    fetch: number; // readonly
}

// API Response wrapper type
export interface PaginatedResponse<T> {
    count: number;
    next: string | null;
    previous: string | null;
    results: T[];
}

// Update interface - Matches API ThreatIntelRecordUpdate model exactly
export interface ThreatIntelRecordUpdate {
    severity?: SeverityType;
    status?: StatusType;
    tags?: Tags;
    notes?: string | null;
    source?: string | null; // maxLength: 255
}

// Tags structure (not specified in API docs, keeping flexible)
export interface Tags {
    [key: string]: any;
}

// Bulk Actions - Corrected to match API documentation exactly
export interface BulkActionRequest {
    action: BulkActionType; // required: 'update' | 'delete'
    record_ids: number[]; // required array of integers
    updates?: {
        severity?: string; // API shows string type
        status?: string; // API shows string type
        tags?: string[]; // API shows array of strings: ["string"]
        notes?: string;
        source?: string;
    };
}

// Bulk action response (API docs show 200 response but no specific structure)
export interface BulkActionResponse {
    message?: string;
    updated_count?: number;
    deleted_count?: number;
}

// Search Types - Matches IntelSearchRequest model exactly
export interface IntelSearchRequest {
    quick_filter: QuickFilterType; // required, enum with 5 values
    query: string; // required, 1-512 characters
    type?: SearchTypeType; // default: 'all', enum with 3 values
    limit?: number; // default: 500, max: 5000, min: 1
    include_system_info?: boolean; // default: false
    page?: number; // default: 1, min: 1
}

export interface IntelSearchResponse {
    fetch_id: string; // UUID format, required
    quick_filter: string; // required, min length: 1
    endpoints_called: string[]; // required array of strings
    results_preview: any[]; // Array of objects (not strings)
    remaining_daily_calls: number | null;
    per_endpoint_windows: { [key: string]: string | null };
    expires_in_seconds: number; // required
    // ✅ OPTIMIZED: Synchronized typing template layer avoids structural pipeline conflicts
    country_counts?: { [key: string]: number }; 
}

// Save Types - Corrected to match API documentation exactly
export interface IntelSaveRequest {
    fetch_id: string; // UUID from /intel/search, required
    save_all?: boolean; // default: false
    indices?: number[]; // Specific result indices to save (if not save_all)
    only_from?: 'consumer' | 'stealer' | 'breach' | 'darkweb'; // API shows "consumer" as example + our 4 sources
    default_severity?: SeverityType; // default: 'medium', enum with 4 values
    default_status?: 'new' | 'investigating' | 'resolved'; // API shows enum with 3 values (not 4)
}

// Save response - Matches IntelSaveResponse model exactly
export interface IntelSaveResponse {
    fetch_id: number; // Database ID of the fetch record, required
    saved_record_ids: number[]; // required array
    saved_count: number;
}

// List Query Parameters for incidents list endpoint
export interface IncidentListParams {
    severity?: SeverityType; // Filter by severity
    status?: StatusType; // Filter by status
    data_type?: string; // Filter by data type
    source?: string; // Filter by source
    page?: number; // Page number
    page_size?: number; // Results per page
    ordering?: string; // Sort field (prefix - for desc)
}

// Success Response Types
export interface SuccessResponse {
    message: string;
}

export interface ThreatStatsResponse {
    success: boolean;
    data: ThreatStatsData;
}

export interface ThreatStatsData {
    totalItems: number;        // Total count of all threat records
    criticalItems: number;     // Count where severity = 'critical'
    newItems: number;          // Count where status = 'new'
    resolvedItems: number;     // Count where status = 'resolved'
}

// Organization Types - Matches OrganizationCreate model exactly
export interface OrganizationCreate {
    name: string; // required, 1-255 characters
    slug?: string; // pattern: ^[-a-zA-Z0-9_]+$, 1-140 characters
}

// Matches OrganizationList model exactly
export interface OrganizationList {
    id: number; // readonly
    name: string; // required, 1-255 characters
    slug: string; // required, pattern: ^[-a-zA-Z0-9_]+$, 1-140 characters
    external_org_id?: string | null; // maxLength: 255
    member_count: number; // readonly
}

// Organization user type - matches the API response structure
export interface OrganizationUser {
    id: number;
    external_user_id?: string; // Clerk user ID
    email: string | null;
    name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    role: RoleType; // 'admin' | 'member'
    status?: 'active' | 'pending' | 'suspended';
    joined_at?: string; // ISO date string
    last_active?: string | null; // ISO date string or null
    created_at?: string; // ISO date string
    updated_at?: string; // ISO date string
}

// UserInvite interface to match new API
export interface UserInviteRequest {
    email: string;                // required, format: email, min length: 1
    role?: 'admin' | 'member';   // default: 'member'
}

// Update user role request
export interface UpdateUserRoleRequest {
    external_user_id: string;     // Clerk user ID
    role: 'admin' | 'member';     // New role
}

// Update user role response
export interface UpdateUserRoleResponse {
    message: string;
    external_user_id: string;
    role: 'admin' | 'member';
    membership_id: string;
}

// Remove user request
export interface RemoveUserRequest {
    external_user_id?: string;     // Clerk user ID
    external_org_id?: string;
}

// Remove user response
export interface RemoveUserResponse {
    message: string;
    external_user_id: string;
}

// Resend invite request
export interface ResendInviteRequest {
    email: string;                // User email
    role?: 'admin' | 'member';    // Optional role, default: 'member'
}

// Resend invite response
export interface ResendInviteResponse {
    message: string;
    invitation_id: string;
    email: string;
    role: 'admin' | 'member';
}

// Organization Details API Types
export interface OrganizationDetailsResponse {
    success: boolean;
    data: OrganizationDetailsData;
}

export interface OrganizationDetailsData {
    id: string;                    // Organization ID
    name: string;                  // Organization name
    license: LicenseDetails;
    apiUsage: APIUsageDetails;
    seats: SeatsDetails;
    planDetails: PlanDetails;      // New field added
}

export interface LicenseDetails {
    type: string;                  // "NOTIFIER PACKAGE", "Professional", "Enterprise", etc.
    expiresAt: string;            // ISO date string
    daysRemaining: number;        // Days until expiration
}

export interface APIUsageDetails {
    monthlyLimit: number;         // Monthly API call limit
    currentUsage: number;         // Current usage count
    usagePercentage: number;      // Usage percentage
    resetDate: string;            // ISO date string for next reset
    isUnlimited: boolean;         // Whether plan has unlimited calls
}

export interface SeatsDetails {
    total: number;                // Total seats available
    used: number;                 // Seats currently used
    available: number;            // Seats available
    usagePercentage: number;      // Percentage of seats used
}

// New PlanDetails interface
export interface PlanDetails {
    planName: string;             // "NOTIFIER PACKAGE", etc.
    planType: string;             // "('notifier', 'Notifier')", etc.
    unmasking: UnmaskingDetails;
    domains: DomainsDetails;
    features: PlanFeatures;
}

export interface UnmaskingDetails {
    used: number;                 // Number of unmaskings used
    monthlyLimit: number;         // Monthly unmasking limit
    remaining: number;            // Unmaskings remaining
    isUnlimited: boolean;         // Whether unmaskings are unlimited
}

export interface DomainsDetails {
    verified: number;             // Number of verified domains
    maxDomains: number;           // Maximum domains allowed
    canAddMore: boolean;          // Whether more domains can be added
    unlimitedDomains: boolean;    // Whether domains are unlimited
}

export interface PlanFeatures {
    corporateRecords: boolean;    // Access to corporate records
    thirdPartyRecords: boolean;   // Access to third party records
    emailRecords: boolean;        // Access to email records
    realTimeAlerting: boolean;    // Real-time alerting feature
    gradientTracing: boolean;     // Network geo pathing trace option
    incidentSaving: boolean;      // Incident saving feature
    threatProfiling: boolean;     // Threat profiling feature
    threatFeeds: boolean;         // Threat feeds feature
    systemInformation: boolean;   // System information feature
    ipAddresses: boolean;         // IP addresses feature
    telegramIntelligence: boolean; // Telegram Intelligence feature (Informant Plan only)
}

export interface UpdateOrganizationRequest {
    name: string;                 // Organization name (1-255 chars)
}

export interface UpdateOrganizationResponse {
    success: boolean;
    message: string;
    data: OrganizationDetailsData;
}

// Constants for UI - Corrected to match API enums
export const SEVERITY_OPTIONS: { value: SeverityType; label: string; color: string }[] = [
    { value: 'low', label: 'Low', color: 'green' },
    { value: 'medium', label: 'Medium', color: 'yellow' },
    { value: 'high', label: 'High', color: 'orange' },
    { value: 'critical', label: 'Critical', color: 'red' }
];

// Status options to match API enum values
export const STATUS_OPTIONS: { value: StatusType; label: string; color: string }[] = [
    { value: 'new', label: 'New', color: 'blue' },
    { value: 'investigating', label: 'Investigating', color: 'yellow' },
    { value: 'resolved', label: 'Resolved', color: 'green' },
    { value: 'false_positive', label: 'False Positive', color: 'gray' }
];

export interface DashboardStats {
    totalSearches: number;
    activeAlerts: number;
    savedItems: number;
    apiCallsRemaining: number;
}

export interface DashboardStatsResponse {
    success: boolean;
    data: DashboardStats;
}

// Threat Summary Types
export interface ThreatSummaryItem {
    category: string;
    count: number;
    percentage: number;
    trend: 'up' | 'down' | 'stable';
    trendValue: number;
}

export interface ThreatSummaryResponse {
    success: boolean;
    data: ThreatSummaryItem[];
}

// Threat Data Types
export interface ThreatDataItem {
    type: string;
    count: number;
    percentage: number;
    trend: 'up' | 'down' | 'stable';
    trendValue: number;
    description: string;
}

export interface ThreatDataSources {
    consumer: number;
    corporate: number;
    email: number;
    ip: number;
}

export interface ThreatDataBreakdown {
    dataTypes: ThreatDataItem[];
    sources: ThreatDataSources;
}

export interface ThreatDataResponse {
    success: boolean;
    data: ThreatDataBreakdown;
}

// Map Data API Types
export interface MapDataLocation {
    ip: string;
    country: string;
    countryCode: string;
    city: string;
    region?: string;
    lat: number;
    lng: number;
    threatCount?: number;
    severity?: SeverityType;
    dataType?: string;
    lastSeen?: string;
    source?: SourceType;
}

export interface MapDataCountryCounts {
    [countryName: string]: number;
}

export interface MapDataResponse {
    success: boolean;
    data: {
        locations: MapDataLocation[];
        countryCounts: MapDataCountryCounts;
        totalIPs: number;
        publicIPs: number;
        geolocatedIPs: number;
    };
}

// Watchlist Types
export type WatchlistEntryType = 'domain' | 'email' | 'vip' | 'computername' | 'ip';
export type WatchlistStatus = 'enabled' | 'disabled';
export type WatchlistAction = 'enable' | 'disable';

export interface WatchlistItem {
    id: number;
    entry_type: WatchlistEntryType;
    entry: string;
    status: WatchlistStatus;
    added_at: string; // ISO date string
    notify_email: string;
}

export interface WatchlistAddRequest {
    entry_type: WatchlistEntryType;
    entry: string;
    include_usernames?: boolean;
    include_passwords?: boolean;
    consumer_alerts?: boolean;
    corporate_alerts?: boolean;
}

export interface WatchlistAddResponse {
    success: boolean;
    id: number;
    entry_type: string;
    entry: string;
    remaining_type_balance: number;
}

export interface WatchlistListParams {
    entry_type?: WatchlistEntryType;
    status?: WatchlistStatus;
    page?: number;
    limit?: number; // max 100, default 50
}

export interface WatchlistListResponse {
    success: boolean;
    page: number;
    limit: number;
    total: number;
    organization_name: string;
    results: WatchlistItem[];
}

export interface WatchlistToggleRequest {
    action: WatchlistAction;
}

export interface WatchlistToggleResponse {
    success: boolean;
    id: number;
    action: string;
    message: string;
}

export interface WatchlistRemoveResponse {
    success: boolean;
    id: number;
    entry_type: string;
    remaining_type_balance: number;
    message: string;
}

export interface WatchlistEntryTypesResponse {
    success: boolean;
    entry_types: {
        [key: string]: string;
    };
}

// Constants for UI
export const WATCHLIST_ENTRY_TYPE_OPTIONS: {
    value: WatchlistEntryType;
    label: string;
    color: string;
    icon: string
}[] = [
        { value: 'domain', label: 'Domain', color: 'blue', icon: 'globe' },
        { value: 'email', label: 'Email', color: 'green', icon: 'mail' },
        { value: 'vip', label: 'VIP', color: 'purple', icon: 'crown' },
        { value: 'computername', label: 'Computer Name', color: 'orange', icon: 'monitor' },
        { value: 'ip', label: 'IP Address', color: 'red', icon: 'server' }
    ];

export const WATCHLIST_STATUS_OPTIONS: { value: WatchlistStatus; label: string; color: string }[] = [
    { value: 'enabled', label: 'Enabled', color: 'green' },
    { value: 'disabled', label: 'Disabled', color: 'gray' }
];

// Threat Feed Types
export interface ThreatFeedPost {
    id: string;
    uuid: string;
    title: string;
    category: string;
    network: string;
    published_at: string; // ISO date string
    victim_organization?: string;
    victim_industry?: string;
    threat_actor_names_cached: string[];
    content?: string;
    summary?: string;
    tags?: string[];
    source_url?: string;
    country?: string;
    region?: string;
}

export interface ThreatFeedListParams {
    page?: number; // Page number (1-based)
    limit?: number; // Results per page (1-100)
    start_date?: string; // Start date filter (YYYY-MM-DD)
    end_date?: string; // End date filter (YYYY-MM-DD)
    category?: string; // Category filter (max 2 values, comma-separated)
    industry?: string; // Industry filter (max 2 values, comma-separated)
    network?: string; // Network filter (comma-separated)
}

export interface ThreatFeedListResponse {
    success: boolean;
    mode: string; // default: posts
    remaining_threat_feed_calls: number;
    results: ThreatFeedPost[];
    total?: number;
    page?: number;
    limit?: number;
}

export interface ThreatFeedSearchParams {
    query: string; // Search query text (required)
    limit?: number; // Maximum results
    category?: string; // Category filter
    industry?: string; // Industry filter
    network?: string; // Network filter
}

export interface ThreatFeedSearchResponse {
    success: boolean;
    results: ThreatFeedPost[];
    search_query: string;
    total_found: number;
}

export interface ThreatFeedStatsParams {
    start_date?: string | undefined; // Start date filter (YYYY-MM-DD)
    end_date?: string | undefined; // End date filter (YYYY-MM-DD)
}

export interface ThreatFeedStats {
    total_posts: number;
    categories: Record<string, number>;
    industries: Record<string, number>;
    networks: Record<string, number>;
    top_threat_actors: Record<string, number>;
    countries: Record<string, number>;
}

export interface ThreatFeedStatsResponse {
    success: boolean;
    stats: ThreatFeedStats;
}

export type TaxonomyType = 'categories' | 'industries' | 'networks';

export interface ThreatFeedTaxonomyParams {
    type: TaxonomyType; // Taxonomy type (required)
    start_date?: string; // Start date filter (YYYY-MM-DD)
    end_date?: string; // End date filter (YYYY-MM-DD)
    limit?: number; // Maximum results (1-100)
}

export interface TaxonomyItem {
    value: string;
    cnt: number; // count
}

export interface ThreatFeedTaxonomyResponse {
    success: boolean;
    mode: string; // default: taxonomy
    taxonomy: string;
    remaining_threat_feed_calls: number;
    results: TaxonomyItem[];
}

// Threat Feed Filters
export interface ThreatFeedFilters {
    search: string;
    category: string[];
    industry: string[];
    network: string[];
    dateRange: {
        startDate: string | null; // YYYY-MM-DD format
        endDate: string | null; // YYYY-MM-DD format
        preset: 'all' | '7d' | '30d' | '90d' | 'custom';
    };
}

export const defaultThreatFeedFilters: ThreatFeedFilters = {
    search: '',
    category: [],
    industry: [],
    network: [],
    dateRange: {
        startDate: null,
        endDate: null,
        preset: 'all'
    }
};

// Constants
export const THREAT_FEED_NETWORKS = [
    'telegram',
    'darkweb',
    'forums',
    'twitter',
    'discord',
    'underground'
] as const;

export const THREAT_FEED_CATEGORIES = [
    'ransomware',
    'malware',
    'data_breach',
    'phishing',
    'fraud',
    'cybercrime',
    'apt',
    'vulnerability'
] as const;

export const THREAT_FEED_INDUSTRIES = [
    'healthcare',
    'finance',
    'education',
    'government',
    'technology',
    'manufacturing',
    'retail',
    'energy',
    'transportation',
    'telecommunications'
] as const;

// Public Search Types
export type SearchType = 'email' | 'username' | 'domain' | 'ip';
export type RiskLevel = 'Low' | 'Medium' | 'High' | 'Critical';

export interface PublicSearchRequest {
    query: string; // The value to search for (email, username, domain, or IP address)
    search_type: SearchType; // Type of search to perform
}

export interface PublicSearchSummary {
    total_infections: number; // Total number of malware infections found
    employees_compromised: number; // Number of employees compromised
    users_compromised: number; // Number of users compromised
    exposed_urls: number; // Number of exposed URLs found
    password_strength_score: number; // Password strength score (0-100)
    top_stealer_family: string; // Most common stealer family found
    last_compromise_date: string; // ISO date of most recent compromise
    risk_level: RiskLevel; // Overall risk assessment
}

export interface PublicSearchResponse {
    success: boolean;
    summary: PublicSearchSummary;
}

export interface PublicSearchErrorResponse {
    success: false;
    error: string;
    details?: string; // Additional error details
    retry_after?: number; // Seconds to wait before retrying (for rate limits)
}

// UI-friendly result interface (transformed from API response)
export interface SearchResult {
    query: string;
    totalInfections: number;
    employeesCompromised: number;
    usersCompromised: number;
    exposedUrls: number;
    passwordStrengthScore: number;
    topStealerFamily: string;
    lastCompromiseDate: string;
    riskLevel: RiskLevel;
    // Calculated fields
    totalCompromised: number;
    passwordStrength: 'Excellent' | 'Good' | 'Fair' | 'Poor';
    lastSeen: string; // Formatted date
}

// Domain Verification Types
export type DomainVerificationMethod = 'dns' | 'email';
export type DomainVerificationStatus = 'pending' | 'verified' | 'failed' | 'expired';

export interface DomainVerificationItem {
    id: number;
    domain: string;
    status: DomainVerificationStatus;
    method: DomainVerificationMethod;
    verified_at?: string | null;
    expires_at: string;
    created_at: string;
    is_verified: boolean;
}

export interface DomainVerificationInstructions {
    method?: string;
    instructions?: string;
    email?: string;
    domain_requirement?: string;
    txt_record?: {
        name: string;
        value: string;
    };
    [key: string]: any;
}

export interface DomainVerificationPlanLimits {
    verified: number;
    max_domains: number;
    unlimited_domains: boolean;
    can_add_more: boolean;
}

// API Request Types
export interface DomainVerificationInitiateRequest {
    domain: string;
    method: DomainVerificationMethod;
    admin_email?: string;
}

export interface DomainVerificationCheckRequest {
    verification_id: number;
}

// API Response Types
export interface DomainVerificationInitiateResponse {
    success: boolean;
    verification_id: number;
    domain: string;
    method: string;
    instructions: DomainVerificationInstructions;
    status: string;
    expires_at: string;
}

export interface DomainVerificationCheckResponse {
    success: boolean;
    verified: boolean;
    status: string;
    message: string;
    verified_at?: string;
}

export interface DomainVerificationListResponse {
    success: boolean;
    verifications: DomainVerificationItem[];
    plan_limits: DomainVerificationPlanLimits;
}

export interface DomainVerificationInstructionsResponse {
    success: boolean;
    verification_id: number;
    domain: string;
    method: string;
    status: string;
    instructions: DomainVerificationInstructions;
}

export interface DomainVerificationDeleteResponse {
    success: boolean;
    message: string;
}

export interface DomainVerificationEmailConfirmResponse {
    success: boolean;
    message: string;
    verified_at?: string;
}

export type TabId = "users" | "details" | "domains" | "password";

export type TabItem = {
    id: TabId;
    label: string;
    icon: React.ElementType; // lucide icon type compatible
};

// ==========================================
// --- Reports API Types ---
// ==========================================

export interface ExposureEvent {
    application: string;
    records: number;
    type: 'combolist' | 'stealer';
    date: string; // YYYY-MM-DD
}

export interface IncidentTimelinePoint {
    Year: number;
    Month: number;
    Count: number;
    source: 'combolist' | 'stealer';
}

export interface ReportKPI {
    description: string;
    value: number;
    pct_delta: number;
}

export interface DomainReportResults {
    recent_exposure: {
        description: string;
        combolists_count: number;
        stealers_count: number;
    };
    latest_corporate_events: ExposureEvent[];
    latest_consumer_events: ExposureEvent[];
    consumer_incident_timeline: IncidentTimelinePoint[];
    corporate_incident_timeline: IncidentTimelinePoint[];
    corporate_leaks_count: ReportKPI;
    consumer_leaks_count: ReportKPI;
    infected_ip_addresses_count: ReportKPI;
    affected_computers_count: ReportKPI;
    total_leaks_count: ReportKPI;
}

export interface DomainReportResponse {
    success: boolean;
    target_domain: string;
    results: DomainReportResults;
}